import { Component } from 'react';
import Head from 'next/head';
import { request } from 'graphql-request';
import * as moment from 'moment';
import dynamic from 'next/dynamic'
import BlockFeatured from '~/components/blockFeatured/blockFeatured';
import BlockParser from '~/components/blockParser/blockParser';
import CarouselEpisodes from '~/components/carouselEpisodes/carouselEpisodes';
import PageWrapper from '~/components/wrappers/pageWrapper';
import Share from '~/components/share/share';
import SiteSettings from '~/siteSettings';
import {
  getBlocksFromQueue,
  getContentPaths,
  getFormattedSchedule,
  get,
  getStructuredData,
  getAlias
} from '~/utils/utils';

// GQL
import GET_EPISODE_SCHEDULE from '~/graphql/GET_EPISODE_SCHEDULE.graphql';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

class EpisodeSchedule extends Component {
  constructor(props) {
    super(props);
    const data = props.data.nodeQuery;
    const schedule = {
      monday: data.monday,
      tuesday: data.tuesday,
      wednesday: data.wednesday,
      thursday: data.thursday,
      friday: data.friday
    };
    const formattedSchedule = getFormattedSchedule(schedule);
    const offset = moment().isDST() ? -420 : -480;
    const dayNow = moment().utcOffset(offset).format('M/D').toLowerCase();
    const currentDayIndex = formattedSchedule.findIndex((elem) => elem && elem.displayDate === dayNow);
    const currentDay = formattedSchedule[currentDayIndex !== -1 ? currentDayIndex : 0].day;
    const content = schedule[currentDay][0];
    const range = `
      ${moment.parseZone(schedule?.monday?.[0]?.field_display_date).format('MMMM')} ${moment.parseZone(schedule?.monday?.[0]?.field_display_date).format('DD')}-${moment.parseZone(schedule?.friday?.[0]?.field_display_date).format('DD')}
    `;

    this.state = {
      currentDay,
      content,
      formattedSchedule,
      schedule,
      range
    };
  }

  setEpisode = (e) => {
    const { schedule } = this.state;
    const offset = moment(schedule[e?.currentTarget?.dataset?.day][0]?.field_display_date).isDST() ? -420 : -480;

    this.setState({
      content: schedule[e?.currentTarget?.dataset?.day][0],
      currentDay: moment(schedule[e?.currentTarget?.dataset?.day][0]?.field_display_date).utcOffset(offset).format('dddd').toLowerCase()
    });
  };

  render() {
    const { router, sitePrimaryRsid, baseUrl, relatedData, blocks } = this.props;
    const { content, formattedSchedule, currentDay, range } = this.state;
    const path = router.asPath;

    return (
      <>
        <Head>
          <title>{ content.title } | { SiteSettings.siteName }</title>
          <meta name="title" content={ content.title } />
          <link rel='canonical' href={`${ baseUrl }${ path }`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={ content.title } />
          <meta name="description" content={ content.field_subhead } />
          <meta property="og:url" content={ path } />
          <meta property="og:title" content={ content.title } />
          <meta property="og:image" content={ content?.field_image?.field_image?.social?.url } />
          <meta property="og:description" content={ content.field_subhead } />
          <link rel="image_src" href={ content?.field_image?.field_image?.social?.url } />
          <meta name="twitter:url" content={ path } />
          <meta name="twitter:image:width" content="1920" />
          <meta name="twitter:image:height" content="1080" />
          <meta name="twitter:image" content={ content?.field_image?.field_image?.social?.url } />
          <meta property="article:published_time" content={ content?.field_display_date } />
          <meta name="keywords" content={ SiteSettings.defaultKeywords } />
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
          />
        </Head>
        {/* Episode Selector */}
        <CarouselEpisodes
          range={ range }
          setEpisode={ this.setEpisode }
          title='this week on the show'
          current={ content }
          schedule={ formattedSchedule }
        ></CarouselEpisodes>
        {/* Episode */}
        <div className='constrain'>
          <div className='content__share'>
            <Share
              title={ content.title }
            />
          </div>
          { content &&
          <BlockFeatured
            items={ [content, ...relatedData[currentDay].resultRow] }
            seeMore
            seeMoreAt={ 2 }
          />
          }
        </div>
        <div data-ad-ref='leader-plus-flex-top' className='bidbarrel-ad'></div>
        <div data-ad-ref='mobile-plus-flex-top' className='bidbarrel-ad'></div>
        { blocks?.entitySubQueueQuery?.items &&
          <BlockParser
            blockItems={ blocks.entitySubQueueQuery.items }
          />
        }
        <div data-ad-ref='leader-plus-flex-bottom' className='bidbarrel-ad'></div>
        <div data-ad-ref='mobile-plus-flex-bottom' className='bidbarrel-ad'></div>
        <Utag pageType='episode_landing' siteSection='show info' data={{ sitePrimaryRsid }} />
        <BidBarrelReact
          data={ content }
          mapToAdUnits={{
            mobile: [
              'mobile-plus-flex-top',
              'mobile-plus-flex-middle',
              'mobile-plus-flex-bottom'
            ],
            desktop: [
              'leader-plus-flex-top',
              'leader-plus-flex-middle',
              'leader-plus-flex-inc',
              'leader-plus-flex-bottom'
            ]
          }}
        />
      </>
    );
  }
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/episodes/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'episode_schedule'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const aliasData = await getAlias({
    alias: `/episodes/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_EPISODE_SCHEDULE, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Related Items
  const GET_RELATED = `
    query($machine_name: String!, $display: String!, $itemsPerPage: Int, $offset: Int) {
      monday: viewQuery(machine_name: $machine_name, display: $display, itemsPerPage: $itemsPerPage, offset: $offset, viewArgs: ["${ data?.nodeQuery?.monday?.[0]?.field_episode?.tid }"]) {
        resultRow {
          ...relatedArticle
          ...relatedVideo
          ...relatedGallery
          ...relatedRecipe
          ...relatedVertical
          # count
        }
      }
      tuesday: viewQuery(machine_name: $machine_name, display: $display, itemsPerPage: $itemsPerPage, offset: $offset, viewArgs: ["${ data?.nodeQuery?.tuesday?.[0]?.field_episode?.tid }"]) {
        resultRow {
          ...relatedArticle
          ...relatedVideo
          ...relatedGallery
          ...relatedRecipe
          ...relatedVertical
          # count
        }
      }
      wednesday: viewQuery(machine_name: $machine_name, display: $display, itemsPerPage: $itemsPerPage, offset: $offset, viewArgs: ["${ data?.nodeQuery?.wednesday?.[0]?.field_episode?.tid }"]) {
        resultRow {
          ...relatedArticle
          ...relatedVideo
          ...relatedGallery
          ...relatedRecipe
          ...relatedVertical
          # count
        }
      }
      thursday: viewQuery(machine_name: $machine_name, display: $display, itemsPerPage: $itemsPerPage, offset: $offset, viewArgs: ["${ data?.nodeQuery?.thursday?.[0]?.field_episode?.tid }"]) {
        resultRow {
          ...relatedArticle
          ...relatedVideo
          ...relatedGallery
          ...relatedRecipe
          ...relatedVertical
          # count
        }
      }
      friday: viewQuery(machine_name: $machine_name, display: $display, itemsPerPage: $itemsPerPage, offset: $offset, viewArgs: ["${ data?.nodeQuery?.friday?.[0]?.field_episode?.tid }"]) {
        resultRow {
          ...relatedArticle
          ...relatedVideo
          ...relatedGallery
          ...relatedRecipe
          ...relatedVertical
          # count
        }
      }
    }
    fragment image on image {
      field_image {
        alt
        ... on file {
          sm: rendition(image_style: "275x154", full_path: true) {
            url
            width
            height
          }
          lg: rendition(image_style: "469x265", full_path: true) {
            url
            width
            height
          }
        }
      }
    }
    fragment relatedVideo on video {
      __typename
      nid
      path
      title
      field_subhead
      body
      field_category {
        ... category
      }
      field_image {
        ...image
      }
      field_video_mpx_id {
        hls_streaming_url
        title
        source_width
        background_video
      }
    }
    fragment relatedRecipe on recipe {
      __typename
      nid
      path
      title
      field_subhead
      body
      field_category {
        ... category
      }
      field_image {
        ...image
      }
    }
    fragment relatedArticle on article {
      __typename
      nid
      path
      title
      field_subhead
      body
      field_category {
        ... category
      }
      field_image {
        ...image
      }
    }
    fragment relatedGallery on gallery {
      __typename
      nid
      path
      title
      field_subhead
      body
      field_category {
        ... category
      }
      field_image {
        ...image
      }
    }
    fragment relatedVertical on vertical_page {
      __typename
      nid
      path
      title
      field_subhead
      body
      field_category {
        ... category
      }
      field_secondary_image {
        ...image
      }
    }
    fragment category on category {
      tid: uuid
      name
      path
    }

  `;

  const relatedData = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_RELATED, {
    machine_name: 'episode_related',
    display: 'master',
    itemsPerPage: 8,
    offset: 0
  });

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'episode',
    limit: 100
  });

  return {
    props: {
      data,
      relatedData,
      blocks
    },
    revalidate: 1
  }
}

export default PageWrapper(EpisodeSchedule);
